// src/routes/userRoutes.js

import React, { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import Layout from './userLayout/Layout';
import MainPage from './site/MainPage';
import USER_PATHS from './USER_PATHS';
import PrivateRoute from './userLayout/PrivateRoute';
import Page404 from './site/Page404';
import Preloader from "../../components/Preloader";

const About = lazy(() => import('./site/About'));
const CartPage = lazy(() => import('./Order/CartPage'));
const OrderForm = lazy(() => import('./Order/OrderForm'));
const LoginPage = lazy(() => import('./auth/LoginPage'));
const RegisterPage = lazy(() => import('./auth/RegisterPage.tsx'));
const SearchResultPage = lazy(() => import('./searchResult/SearchResultPage'));
const CategoryPage = lazy(() => import('./category/CategoryPage'));
const CategoriesPage = lazy(() => import('./category/CategoriesPage'));
const OrdersPage = lazy(() => import('./Order/OrderPage'));
const OrderDetails = lazy(() => import('./Order/OrderDetails'));
const OrderTracking = lazy(() => import('./Order/OrderTracking'));
const ProfileLayout = lazy(() => import('./userLayout/profile/ProfileLayout'));
const MyReviews = lazy(() => import('./review/MyReviews'));
const UserOverview = lazy(() => import('./profile/UserOverview'));
const ManageAccount = lazy(() => import('./profile/ManageAccount'));
const ReturnAndRefund = lazy(() => import('./profile/ReturnAndRefund'));
const WishlistAndStores = lazy(() => import('./profile/WishlistAndStores'));
const DisputeCenter = lazy(() => import('./profile/DisputeCenter'));
const ProductDetails = lazy(() => import('./products/ProductDetails'));
const TermsOfService = lazy(() => import('./site/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./site/PrivacyPolicy'));
const HelpCenter = lazy(() => import('./site/HelpCenter'));
const TrackOrder = lazy(() => import('./site/TrackOrder'));
const ReturnsRefunds = lazy(() => import('./site/ReturnsRefunds'));
const ShippingInfo = lazy(() => import('./site/ShippingInfo'));
const Vauchers = lazy(() => import("./profile/Vouchers"));
const Payments = lazy(() => import("./profile/Payments"));
const Address = lazy(() => import("./profile/Addresses"));
const Notifications = lazy(() => import("./profile/Notifications"));
const AddressPage = lazy(() => import('./Order/AddressPage'));
const ReviewOrder = lazy(() => import('./Order/ReviewOrder'));
const OrderSuccess = lazy(() => import('./Order/OrderSuccess'));
const PayStationReturn = lazy(() => import('./Order/PayStationReturn'));
const ForgotPassword = lazy(() => import('./auth/ForgotPassword'));
const ContactUs = lazy(() => import('./site/Contact'));
const AuthSuccess = lazy(() => import('./auth/AuthSuccess'));
const CookiePolicy = lazy(() => import("./site/CookiePolicy"));
const ProductReviewWrite = lazy(() => import("./products/ProductReviewWrite"));
const CampaignPage = lazy(() => import("./site/CampaignPage"));
const AccountDeletion = lazy(() => import("./site/AccountDeletion"));
const AppDownloadRedirect = lazy(() => import("./site/AppDownloadRedirect"));
const VerifyEmail = lazy(() => import('./auth/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const CompleteSocialClaim = lazy(() => import('./auth/VerifyEmail').then(m => ({ default: m.CompleteSocialClaim })));

const S = (node) => (
  <Suspense fallback={<Preloader variant="inline" containerClassName="min-h-[50vh]" />}>
    {node}
  </Suspense>
);

export const userRoutes = [
  { path: "app", element: S(<AppDownloadRedirect />) },
  {
    element: <Layout />,
    children: [
      { path: "/", index: true, element: <MainPage /> },
      { path: USER_PATHS.PRODUCTS, element: S(<ProductDetails />) },
      { path: "product/:id", element: S(<ProductDetails />) },
      { path: USER_PATHS.REVIEWWRITE, element: <PrivateRoute>{S(<ProductReviewWrite />)}</PrivateRoute> },
      { path: USER_PATHS.SEARCH, element: S(<SearchResultPage />) },
      { path: USER_PATHS.CATEGORY, element: S(<CategoryPage />) },
      { path: USER_PATHS.CAMPAIGN, element: S(<CampaignPage />) },
      { path: USER_PATHS.CATEGORIES, element: S(<CategoriesPage />) },
      { path: USER_PATHS.ABOUT.slice(1), element: S(<About />) },   // slice(1) to remove '/'
      { path: USER_PATHS.CONTACT.slice(1), element: S(<ContactUs />) },
      { path: USER_PATHS.CART.slice(1), element: S(<CartPage />) },
      // { path: USER_PATHS.ORDER.slice(1), element: <OrdersPage /> },
      { path: USER_PATHS.ORDER_DETAILS.slice(1), element: <PrivateRoute>{S(<OrderDetails />)}</PrivateRoute> },
      { path: USER_PATHS.CHECKOUT.slice(1), element: <PrivateRoute>{S(<OrderForm />)}</PrivateRoute> },
      { path: USER_PATHS.SHIPPING.slice(1), element: <PrivateRoute>{S(<AddressPage />)}</PrivateRoute> },
      { path: USER_PATHS.REVIEWORDER.slice(1), element: <PrivateRoute>{S(<ReviewOrder />)}</PrivateRoute> },
      { path: USER_PATHS.PAYMENT_RETURN.slice(1), element: <PrivateRoute>{S(<PayStationReturn />)}</PrivateRoute> },
      { path: USER_PATHS.PAYSTATION_RETURN.slice(1), element: <PrivateRoute>{S(<PayStationReturn />)}</PrivateRoute> },
      // { path: USER_PATHS.PROFILE.slice(1), element: <UserDashboardPage /> },
      { path: USER_PATHS.LOGIN.slice(1), element: S(<LoginPage />) },
      { path: USER_PATHS.REGISTER.slice(1), element: S(<RegisterPage />) },
      { path: "auth/success", element: S(<AuthSuccess />) },
      { path: USER_PATHS.VERIFY_EMAIL.slice(1), element: S(<VerifyEmail />) },
      { path: USER_PATHS.COMPLETE_SOCIAL_CLAIM.slice(1), element: S(<CompleteSocialClaim />) },
      { path: USER_PATHS.FORGOT_PASSWORD.slice(1), element: S(<ForgotPassword />) },
      { path: USER_PATHS.TERMS_OF_SERVICE.slice(1), element: S(<TermsOfService />) },
      { path: USER_PATHS.PRIVACY_POLICY.slice(1), element: S(<PrivacyPolicy />) },
      { path: USER_PATHS.ACCOUNT_DELETION.slice(1), element: S(<AccountDeletion />) },
      { path: USER_PATHS.COOKIES_POLICY.slice(1), element: S(<CookiePolicy />) },
      { path: USER_PATHS.HELP_CENTER.slice(1), element: S(<HelpCenter />) },
      { path: USER_PATHS.TRACK_ORDER, element: S(<TrackOrder />) },
      { path: USER_PATHS.RETURNS_REFUNDS.slice(1), element: S(<ReturnsRefunds />) },
      { path: USER_PATHS.SHIPPING_INFO.slice(1), element: S(<ShippingInfo />) },
      { path: '*', element: <Page404 /> },
      {
        element: <PrivateRoute>{S(<ProfileLayout />)}</PrivateRoute>,
        path: USER_PATHS.PROFILE,
        children: [
          { path: USER_PATHS.PROFILE, element: S(<UserOverview />) },
          { path: USER_PATHS.ORDER.split('/').pop(), element: S(<OrdersPage />) },
          { path: USER_PATHS.ORDER_TRACING.split('/').slice(2).join('/'), element: S(<OrderTracking />) },
          { path: USER_PATHS.MANAGEPROFILE.split('/').pop(), element: S(<ManageAccount />) },
          { path: USER_PATHS.RETURN_AND_REFUND.split('/').pop(), element: S(<ReturnAndRefund />) },
          { path: USER_PATHS.WISHLIST_AND_FOLLOWED_STORES.split('/').pop(), element: S(<WishlistAndStores />) },
          { path: USER_PATHS.APPEAL_DISPUTE_CENTER.split('/').pop(), element: S(<DisputeCenter />) },
          { path: USER_PATHS.MY_REVIEWS.split('/').pop(), element: S(<MyReviews />) },
          { path: USER_PATHS.VAUCHERS.split('/').pop(), element: S(<Vauchers />) },
          { path: USER_PATHS.ADDRESS.split('/').pop(), element: S(<Address />) },
          { path: USER_PATHS.PAYMENT.split('/').pop(), element: S(<Payments />) },
          { path: USER_PATHS.NOTIFICATIONS.split('/').pop(), element: S(<Notifications />) },
          {
            path: USER_PATHS.SETTINGS.split('/').pop(),
            element: <Navigate to={USER_PATHS.MANAGEPROFILE.split('/').pop()} replace />,
          },
          { path: '*', element: <Page404 /> },
        ]
      },

    ],
  },
  { path: USER_PATHS.SUCCESSORDER.slice(1), element: <PrivateRoute>{S(<OrderSuccess />)}</PrivateRoute> },

];
